import React from 'react';
import { Search, Filter, Sliders, X } from 'lucide-react';
import { colors } from './constants';

const SearchFilters = ({
  searchTerm,
  setSearchTerm,
  filterActive,
  setFilterActive,
  ageRange,
  setAgeRange,
  experienceYearsRange,
  setExperienceYearsRange,
  experienceFilter,
  setExperienceFilter,
  competenceFilter,
  setCompetenceFilter,
  diplomeFilter,
  setDiplomeFilter,
  langueFilter,
  setLangueFilter,
  allExperienceDomains,
  allCompetences,
  allDiplomes,
  allLangues,
}) => {
  const resetFilters = () => {
    setAgeRange({ min: 18, max: 65 });
    setExperienceYearsRange({ min: 0, max: 20 });
    setExperienceFilter('');
    setCompetenceFilter('');
    setDiplomeFilter('');
    setLangueFilter('');
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-4 mb-8">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            placeholder="Rechercher un demandeur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          className={`px-4 py-2 rounded-lg flex items-center ${
            filterActive 
              ? 'bg-orange-500 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => setFilterActive(!filterActive)}
        >
          <Filter className="h-5 w-5 mr-2" />
          Filtres
        </button>
      </div>

      {filterActive && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h3 className="font-medium text-gray-700 mb-4 flex items-center">
            <Sliders className="h-4 w-4 mr-2" />
            Filtres avancés
          </h3>
          
          {/* Filtre par âge */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Âge (entre {ageRange.min} et {ageRange.max} ans)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="range"
                  min="18"
                  max="65"
                  value={ageRange.min}
                  onChange={(e) => setAgeRange({...ageRange, min: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>18</span>
                  <span>{ageRange.min}</span>
                </div>
              </div>
              <div>
                <input
                  type="range"
                  min="18"
                  max="65"
                  value={ageRange.max}
                  onChange={(e) => setAgeRange({...ageRange, max: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>{ageRange.max}</span>
                  <span>65</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Filtre par années d'expérience */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expérience (entre {experienceYearsRange.min} et {experienceYearsRange.max} ans)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={experienceYearsRange.min}
                  onChange={(e) => setExperienceYearsRange({...experienceYearsRange, min: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>0</span>
                  <span>{experienceYearsRange.min}</span>
                </div>
              </div>
              <div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={experienceYearsRange.max}
                  onChange={(e) => setExperienceYearsRange({...experienceYearsRange, max: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>{experienceYearsRange.max}</span>
                  <span>20+</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domaine d'expérience
              </label>
              <select
                value={experienceFilter}
                onChange={(e) => setExperienceFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Tous les domaines</option>
                {allExperienceDomains.map((domain, index) => (
                  <option key={index} value={domain}>{domain}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Compétence
              </label>
              <select
                value={competenceFilter}
                onChange={(e) => setCompetenceFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Toutes les compétences</option>
                {allCompetences.map((comp, index) => (
                  <option key={index} value={comp}>{comp}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Diplôme
              </label>
              <select
                value={diplomeFilter}
                onChange={(e) => setDiplomeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Tous les diplômes</option>
                {allDiplomes.map((diplome, index) => (
                  <option key={index} value={diplome}>{diplome}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue
              </label>
              <select
                value={langueFilter}
                onChange={(e) => setLangueFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Toutes les langues</option>
                {allLangues.map((langue, index) => (
                  <option key={index} value={langue}>{langue}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end gap-3">
            <button 
              onClick={resetFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
